import { useSectionAccess } from "../auth/useSectionAccess";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { formatDate, formatDateTime, formatMoney, formatNumber, formatUnitPrice } from "../lib/format";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { catalogApi, type CategoryNode, type ProductSummary } from "../api/catalog";
import { importsApi } from "../api/imports";
import { approvalWaitsForAnotherSigner, nextAllowedFor, ordersApi, statusBadgePalette, type LineInput, type Order, type OrderAddress, type OrderLine, type OrderStatus, type QuoteResult } from "../api/orders";
import type { CompanyAddress, CompanyAddressBody } from "../api/admin";
import { tripsApi, type TripView } from "../api/transport";
import type { ApiError } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { useToast } from "../components/Toast";
import { useConfirm, usePrompt } from "../components/ConfirmDialog";
import { OrderStatusBadge } from "../components/OrderStatusBadge";
import { DownloadModal } from "../components/DownloadModal";
import { AddressFormFields } from "../components/AddressFormFields";
import { Select } from "../components/Select";
import { stockStatus, outOfStockSkus } from "../lib/stockStatus";
import { productUnit } from "../lib/productUnit";
import { ErpReadOnlyNote } from "../components/ErpReadOnlyNote";
import Ic from "../components/icons.jsx";
import { ProductionOrders } from "../components/orders/ProductionOrders";
import { OrderErpTerms } from "../components/orders/OrderErpTerms";
import { OrderDispatch } from "../components/orders/OrderDispatch";
import { OrderReservations } from "../components/orders/OrderReservations";
import { OrderProforma } from "../components/orders/OrderProforma";
import { CustomerOrderShipments } from "../components/orders/CustomerOrderShipments";
import { approvedHint } from "../components/orders/approvedHint";

/**
 * Order editor — cart-style for new drafts, full editor for existing orders.
 * New: lines collected locally, posted on first save (avoids empty draft rows).
 * Existing: each line mutates via API; header saves explicitly.
 */
export default function OrderEditorPage() {
  const { canWrite, canFull } = useSectionAccess("orders");
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useToast();
  const confirm = useConfirm();
  const prompt = usePrompt();
  const { state, hasPermission } = useAuth();
  // Suspended company: the server refuses create/submit/clone with
  // COMPANY_SUSPENDED — mirror that in the UI instead of offering dead buttons.
  const suspended = state.status === "authenticated" && state.user.companySuspended;

  const isNew = !id || id === "new";
  const [order, setOrder] = useState<Order | null>(null);
  const [productOptions, setProductOptions] = useState<ProductSummary[]>([]);
  const [tree, setTree] = useState<CategoryNode[] | null>(null);
  const [requestedDelivery, setRequestedDelivery] = useState("");
  const [customerNotes, setCustomerNotes] = useState("");
  const [customerReference, setCustomerReference] = useState("");
  const [salesNotesDraft, setSalesNotesDraft] = useState("");
  const [draftLines, setDraftLines] = useState<LineInput[]>([]);
  const [busy, setBusy] = useState(false);
  const [productSearch, setProductSearch] = useState("");
  const [pickedProduct, setPickedProduct] = useState<ProductSummary | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [quote, setQuote] = useState<QuoteResult | null>(null);

  // Address book for the order's company + the chosen delivery/billing entries.
  const [addresses, setAddresses] = useState<CompanyAddress[]>([]);
  const [deliveryAddressId, setDeliveryAddressId] = useState("");
  const [billingAddressId, setBillingAddressId] = useState("");
  const [addAddressOpen, setAddAddressOpen] = useState<null | "delivery" | "billing">(null);

  // An order from the ERP is shown here and changed only there: no review actions and no notes.
  const canReview = canWrite && hasPermission("orders:transition") && !order?.fromErp;
  const canCreate = canWrite && hasPermission("orders:create");
  const canApprove = canFull && state.status === "authenticated" && state.user.roles.some((r) => ["ADMIN", "SUPERADMIN", "SALES_DIRECTOR"].includes(r));
  const canImport = hasPermission("orders:import");
  const canViewTrips = hasPermission("trips:view");
  // Internal-only: the import digest that produced this order (null for organic orders).
  const [sourceImportId, setSourceImportId] = useState<string | null>(null);
  // The trip this order rides on, if any — loaded best-effort, same
  // as sourceImportId above.
  const [trips, setTrips] = useState<TripView[]>([]);

  // Stock check: soft, placeholder-backed warning for reviewers in the
  // pre-approval window. Purely informational — it never blocks approval (the
  // SALES gate stays unconditional). Swaps to real data via lib/stockStatus.
  const showStockWarning = canReview && !!order && (order.status === "SUBMITTED" || order.status === "IN_REVIEW");
  const outOfStockSkuList = showStockWarning && order ? outOfStockSkus(order.lines.map((l) => l.productSku)) : [];

  // Lifecycle actions depend on who is looking: the approval is withheld from
  // whoever entered the order, because it takes a second signature.
  const viewerId = state.status === "authenticated" ? state.user.id : null;
  // Whom the administrator lets order without approval sends their own order to the ERP at once, and approves an own
  // order that is already waiting.
  const withoutApproval = hasPermission("orders:without_approval");
  const ownApproval = withoutApproval && !!order && order.createdBy === viewerId;
  const reviewTargets = order
    ? nextAllowedFor(order, viewerId, withoutApproval).filter((to) => to !== "APPROVED" || canApprove || ownApproval)
    : [];
  const waitsForAnotherSigner = !!order && approvalWaitsForAnotherSigner(order, viewerId, withoutApproval);

  useEffect(() => {
    void catalogApi
      .listProducts({ status: "PUBLISHED", page: 0, size: 2000, locale: "en" })
      .then((p) => setProductOptions(p.content.filter(Boolean)));
    void catalogApi.tree("en").then(setTree).catch(() => undefined);
  }, []);

  // Never call the imports endpoint for customers — it is gated to orders:import.
  useEffect(() => {
    if (isNew || !id || !canImport) { setSourceImportId(null); return; }
    let live = true;
    void importsApi.byOrder(id)
      .then((orderImport) => { if (live) setSourceImportId(orderImport?.id ?? null); })
      .catch(() => undefined); // banner is best-effort; never disturb the page
    return () => { live = false; };
  }, [id, isNew, canImport]);

  // Trip link: GET /transport/trips?orderId=
  // resolves "which trip is this order on" as a single targeted backend
  // lookup (see TripService#list's javadoc), the same approach
  // DeliveryDetailPage uses, rather than loading every trip and searching
  // them here.
  // Best-effort — a caller without trips:view never reaches this at all,
  // since the row it feeds is gated on the same permission.
  useEffect(() => {
    if (isNew || !id || !canViewTrips) { setTrips([]); return; }
    void tripsApi.list({ orderId: id }).then(setTrips).catch(() => setTrips([]));
  }, [isNew, id, canViewTrips]);

  useEffect(() => {
    if (isNew || !id) return;
    void ordersApi.get(id).then((o) => {
      setOrder(o);
      setRequestedDelivery(o.requestedDeliveryDate ?? "");
      setCustomerNotes(o.customerNotes ?? "");
      setCustomerReference(o.customerReference ?? "");
      setSalesNotesDraft(o.salesNotes ?? "");
    }).catch((e: ApiError) => toast.error(e.message));
  }, [id, isNew, toast]);

  // Load the address book for the order's company (new: customer's own company)
  // and preselect each purpose's default. Only needed while the order is editable.
  const orderCompanyId = order?.companyId
    ?? (state.status === "authenticated" ? state.user.companyId : null);
  useEffect(() => {
    if (!orderCompanyId) { setAddresses([]); return; }
    let live = true;
    void ordersApi.listCompanyAddresses(orderCompanyId)
      .then((list) => {
        if (!live) return;
        setAddresses(list);
        // Preselect the company defaults only when nothing is chosen yet.
        setDeliveryAddressId((cur) => cur || list.find((a) => a.defaultDelivery)?.id || "");
        setBillingAddressId((cur) => cur || list.find((a) => a.defaultBilling)?.id || "");
      })
      .catch(() => { if (live) setAddresses([]); });
    return () => { live = false; };
  }, [orderCompanyId]);

  // Live cart pricing (debounced) — catalog → discount → net → total.
  useEffect(() => {
    const companyId = state.status === "authenticated" ? state.user.companyId : null;
    const src = order ? order.lines : draftLines;
    const items = src.map((l) => ({
      productId: "productId" in l ? ((l.productId as string | null) ?? undefined) : undefined,
      quantity: Number(l.quantity),
    }));
    if (!companyId || items.length === 0) { setQuote(null); return; }
    let live = true;
    const t = setTimeout(() => {
      void ordersApi.quote(companyId, items).then((qr) => { if (live) setQuote(qr); }).catch(() => undefined);
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [draftLines, order, state]);

  const imageByCategory = useMemo(() => {
    const map = new Map<string, string>();
    const walk = (ns: CategoryNode[]) => ns.forEach((n) => {
      if (n.imageUrl) map.set(n.id, n.imageUrl);
      walk(n.children);
    });
    if (tree) walk(tree);
    return map;
  }, [tree]);

  const productById = useMemo(() => {
    const m = new Map<string, ProductSummary>();
    productOptions.forEach((p) => m.set(p.id, p));
    return m;
  }, [productOptions]);

  if (state.status !== "authenticated") return null;
  const customerCompanyId = state.user.companyId;

  const editable = canCreate && (isNew || order?.status === "DRAFT");
  // The trips this order rides on. `trips` is the server-filtered result for
  // this exact order (see the effect above) — empty covers a new/unsaved draft
  // (never fetched) or a saved order that simply isn't on one.
  //
  // Plural on purpose: an order is deliberately not guarded against riding two
  // trips, because a 40 t order legitimately splits across two lorries. Reading
  // `trips[0]` here reported one lorry and hid the rest.
  const totalLines = order?.lines?.length ?? draftLines.length;
  const totalQty = (order?.lines ?? draftLines).reduce((sum, l) => sum + Number(l.quantity ?? 0), 0);
  /**
   * Ukupan iznos: zbir snimljenih stavki kad ih porudžbina nosi, inače živa procena.
   *
   * Snimak je zapis o tome po čemu je prodato; cenovnik je ono što važi danas. Za nacrt
   * koji se sastavlja druga je tačna, za porudžbinu iz istorije prva.
   */
  const snimljeneStavke = (order?.lines ?? []).filter((l) => l.lineTotal != null);
  const ukupanIznos = snimljeneStavke.length > 0
    ? snimljeneStavke.reduce((z, l) => z + Number(l.lineTotal ?? 0), 0)
    : quote?.orderTotal ?? null;
  const valutaPorudzbine = (order?.lines ?? []).find((l) => l.currency)?.currency ?? quote?.currency;
  const filteredOptions = productSearch.trim().length === 0
    ? productOptions.slice(0, 8)
    : productOptions
        .filter((p) =>
          `${p.sku} ${p.name ?? ""}`
            .toLowerCase()
            .includes(productSearch.toLowerCase()),
        )
        .slice(0, 12);

  function addDraftLine(p: ProductSummary, qty: number) {
    if (!editable) return;
    if (qty <= 0) { toast.error(t("orderEditor.error.quantityPositive", "Quantity must be > 0.")); return; }
    setDraftLines((prev) => [
      ...prev,
      { productId: p.id, freeTextDescription: null, quantity: qty, unit: productUnit(p), customerNotes: null },
    ]);
    resetPicker();
  }

  function removeDraftLine(idx: number) {
    if (!editable) return;
    setDraftLines((prev) => prev.filter((_, i) => i !== idx));
  }

  async function addLineToExisting(p: ProductSummary, qty: number) {
    if (!canWrite || !order) return;
    if (qty <= 0) { toast.error(t("orderEditor.error.quantityPositive", "Quantity must be > 0.")); return; }
    setBusy(true);
    try {
      const updated = await ordersApi.addLine(order.id, {
        productId: p.id, freeTextDescription: null, quantity: qty, unit: productUnit(p),
      });
      setOrder(updated);
      resetPicker();
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  async function changeLineQty(lineId: string, newQty: number) {
    if (!canWrite || !order) return;
    if (newQty <= 0) return;
    // BE doesn't have a direct quantity-update; remove + re-add to mutate.
    setBusy(true);
    try {
      const line = order.lines.find((l) => l.id === lineId);
      if (!line) return;
      let updated = await ordersApi.removeLine(order.id, lineId);
      updated = await ordersApi.addLine(order.id, {
        productId: line.productId,
        freeTextDescription: line.freeTextDescription,
        quantity: newQty,
        unit: line.unit,
        customerNotes: line.customerNotes,
      });
      setOrder(updated);
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  async function removeExistingLine(lineId: string) {
    if (!canWrite || !order) return;
    setBusy(true);
    try {
      const updated = await ordersApi.removeLine(order.id, lineId);
      setOrder(updated);
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  async function saveDraftHeader(e: FormEvent) {
    e.preventDefault();
    if (!editable) return;
    setBusy(true);
    try {
      if (isNew) {
        if (!customerCompanyId) {
          toast.error(t("orders.address.noCompany", "Your account isn't linked to a company."));
          return;
        }
        const created = await ordersApi.create({
          companyId: customerCompanyId,
          requestedDeliveryDate: requestedDelivery || undefined,
          customerNotes: customerNotes || undefined,
          customerReference: customerReference || undefined,
          lines: draftLines,
          deliveryAddressId: deliveryAddressId || undefined,
          billingAddressId: billingAddressId || undefined,
        });
        toast.success(t("orderEditor.toast.orderSaved", "Order {{orderNumber}} saved.", { orderNumber: created.orderNumber }));
        navigate(`/orders/${created.id}`, { replace: true });
      } else if (order) {
        const updated = await ordersApi.update(order.id, {
          requestedDeliveryDate: requestedDelivery || null,
          customerNotes,
          customerReference,
          deliveryAddressId: deliveryAddressId || undefined,
          billingAddressId: billingAddressId || undefined,
        });
        setOrder(updated);
        toast.success(t("orderEditor.toast.saved", "Saved."));
      }
    } catch (err) {
      toast.error(orderErrorMessage(err as ApiError));
    } finally {
      setBusy(false);
    }
  }

  /** Localized message for order-flow errors keyed on stable server codes;
   * everything else falls back to the raw server message. */
  function orderErrorMessage(err: ApiError): string {
    switch (err.code) {
      case "COMPANY_SUSPENDED":
        return t("orders.suspendedBanner", "Your account is temporarily blocked for ordering. Please contact your Bankom sales representative.");
      case "ORDER_REJECT_REASON_REQUIRED":
        return t("orderEditor.reject.reasonRequired", "A reason is required to reject an order.");
      default:
        return err.message;
    }
  }

  async function submitOrder() {
    if (!canCreate || !order) return;
    if (totalLines === 0) { toast.error(t("orderEditor.error.addLineBeforeSubmit", "Add at least one line before submitting.")); return; }
    const question = withoutApproval
      ? t("orderErp.confirmSubmitDirect", "Send {{orderNumber}} to the ERP? It is approved at once, with no second signature.", { orderNumber: order.orderNumber })
      : t("orderEditor.confirm.submit", "Submit {{orderNumber}}? Your sales rep will be notified for review.", { orderNumber: order.orderNumber });
    if (!(await confirm({ message: question }))) return;
    setBusy(true);
    try {
      const submitted = await ordersApi.submit(order.id);
      setOrder(submitted);
      toast.success(submitted.status === "APPROVED"
        ? t("orderErp.submittedDirect", "Approved and sent to the ERP.")
        : t("orderEditor.toast.submittedForReview", "Submitted for review."));
    } catch (err) {
      toast.error(orderErrorMessage(err as ApiError));
    } finally {
      setBusy(false);
    }
  }

  async function doTransition(to: OrderStatus) {
    if (!canReview || !order || !reviewTargets.includes(to)) return;
    let notes = salesNotesDraft.trim();
    if (to === "REJECTED") {
      // Mandatory rejection reason — the server refuses
      // REJECTED without one, so the dialog collects it up front.
      const reason = await prompt({
        title: t("orderEditor.reject.title", "Reject order"),
        message: t("orderEditor.reject.message", "Explain why {{orderNumber}} is being rejected. The customer sees this reason and can clone the order to a new draft.", { orderNumber: order.orderNumber }),
        placeholder: t("orderEditor.reject.placeholder", "Reason for rejection…"),
        defaultValue: notes,
        multiline: true,
        required: true,
        danger: true,
        confirmLabel: t("orderEditor.transition.reject", "Reject"),
      });
      if (reason == null || !reason.trim()) return;
      notes = reason.trim();
    } else {
      const meta = transitionMeta(t)[to];
      if (meta?.confirm && !(await confirm({ message: meta.confirm(order.orderNumber), danger: true }))) return;
    }
    setBusy(true);
    try {
      const updated = await ordersApi.transition(order.id, to, notes || undefined);
      setOrder(updated);
      setSalesNotesDraft(updated.salesNotes ?? "");
      toast.success(t("orderEditor.toast.orderMovedTo", "Order moved to {{status}}.", { status: labelForStatus(t, to) }));
    } catch (err) {
      toast.error(orderErrorMessage(err as ApiError));
    } finally {
      setBusy(false);
    }
  }

  async function cloneOrder() {
    if (!canCreate || !order) return;
    setBusy(true);
    try {
      const cloned = await ordersApi.clone(order.id);
      navigate(`/orders/${cloned.id}`, { replace: true });
      toast.success(t("orderEditor.toast.clonedTo", "Cloned to {{orderNumber}}.", { orderNumber: cloned.orderNumber }));
    } catch (err) {
      toast.error(orderErrorMessage(err as ApiError));
    } finally {
      setBusy(false);
    }
  }

  function resetPicker() {
    setPickedProduct(null);
    setProductSearch("");
    setQuantity(1);
  }

  // Inline add-address escape hatch — POSTs to the customer-gated endpoint,
  // refreshes the book, and selects the new entry for the purpose it was added for.
  async function handleAddAddress(purpose: "delivery" | "billing", body: CompanyAddressBody) {
    if (!editable) return;
    if (!orderCompanyId) { toast.error(t("orders.address.noCompany", "Your account isn't linked to a company.")); return; }
    const created = await ordersApi.addCompanyAddress(orderCompanyId, body);
    const list = await ordersApi.listCompanyAddresses(orderCompanyId);
    setAddresses(list);
    if (purpose === "delivery") setDeliveryAddressId(created.id);
    else setBillingAddressId(created.id);
    setAddAddressOpen(null);
    toast.success(t("orders.address.added", "Address added."));
  }

  return (
    <form onSubmit={saveDraftHeader} className="fadein" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head">
        <div>
          <nav className="crumbs" aria-label={t("orderEditor.breadcrumb.aria", "Breadcrumb")}>
            <span style={crumbMuted}>{t("orderEditor.breadcrumb.commercial", "Commercial")}</span>
            <span><Link to="/orders" style={crumbLink}>{t("orderEditor.breadcrumb.orders", "Orders")}</Link></span>
            <span style={crumbCurrent}>{isNew ? t("orderEditor.newOrder", "New order") : order?.orderNumber ?? "…"}</span>
          </nav>
          <h1>{isNew ? t("orderEditor.newOrder", "New order") : order?.orderNumber ?? t("orderEditor.order", "Order")}</h1>
          <div className="sub">
            {order?.companyName ?? state.user.companyName ?? "—"}
            {order?.portalNumber && ` · ${t("orderEditor.requestNumber", "request {{number}}", { number: order.portalNumber })}`}
            {order?.createdAt && ` · ${t("orderEditor.createdOn", "created {{date}}", { date: formatDate(order.createdAt) })}`}
          </div>
        </div>
        <div className="page-actions">
          <button type="button" onClick={() => navigate(-1)} className="btn">
            {t("orderEditor.back", "Back")}
          </button>
          {!isNew && order && (
            <DownloadModal orderId={order.id} orderNumber={order.orderNumber} />
          )}
          {canCreate && !isNew && order?.status === "REJECTED" && (
            <button type="button" onClick={cloneOrder} className="btn" disabled={busy}>
              {t("orderEditor.cloneToDraft", "Clone to draft")}
            </button>
          )}
          {editable && (
            <button type="submit" className="btn" disabled={busy}>
              {isNew ? t("orderEditor.saveDraft", "Save draft") : t("orderEditor.saveHeader", "Save header")}
            </button>
          )}
          {canCreate && !isNew && order?.status === "DRAFT" && (
            <button
              type="button"
              onClick={submitOrder}
              className="btn primary"
              disabled={busy || totalLines === 0 || suspended}
              title={suspended ? t("orders.suspendedBanner", "Your account is temporarily blocked for ordering. Please contact your Bankom sales representative.") : undefined}
            >
              <Ic.send /> {withoutApproval
                ? t("orderErp.submitDirect", "Send to the ERP")
                : t("orderEditor.submitForReview", "Submit for review")}
            </button>
          )}
        </div>
      </div>

      {order?.fromErp && <ErpReadOnlyNote text={t("orderEditor.fromErp")} />}

      {/* Internal-only: this order came from an import digest (sales/admin eyes only). */}
      {sourceImportId && (
        <div className="oi-banner" style={importBanner}>
          <Ic.upload />
          <span>
            {t("orders.import.banner.viaImport")}{" "}
            <Link to={`/orders/imports/${sourceImportId}`} style={importBannerLink}>
              {t("orders.import.banner.viewAnalysis")}
            </Link>
          </span>
        </div>
      )}

      <div style={kpiStrip}>
        <div style={kpiTile}>
          <span style={kpiLabel}>{t("orderEditor.kpi.status", "Status")}</span>
          <strong style={kpiValueLg}>
            {order ? <OrderStatusBadge status={order.status} /> : <span style={{ color: "var(--ink-3)" }}>{t("orderEditor.draftUnsaved", "Draft (unsaved)")}</span>}
          </strong>
        </div>
        <div style={kpiTile}>
          <span style={kpiLabel}>{t("orderEditor.kpi.lines", "Lines")}</span>
          <strong style={kpiValueLg}>{totalLines}</strong>
        </div>
        <div style={kpiTile}>
          <span style={kpiLabel}>{t("orderEditor.kpi.totalQty", "Total qty")}</span>
          <strong style={kpiValueLg}>{formatNumber(totalQty, undefined, { maximumFractionDigits: 0 })}</strong>
        </div>
        <div style={kpiTile}>
          <span style={kpiLabel}>{t("orderEditor.kpi.totalAmount", "Total amount")}</span>
          <strong style={kpiValueLg}>{ukupanIznos != null
            ? money(ukupanIznos, valutaPorudzbine)
            : <span style={{ color: "var(--ink-3)", fontWeight: 400 }}>—</span>}</strong>
        </div>
        <div style={kpiTile}>
          <span style={kpiLabel}>{t("orderEditor.kpi.requestedDelivery", "Requested delivery")}</span>
          <strong style={kpiValueLg}>
            {requestedDelivery ? formatDate(requestedDelivery) : <span style={{ color: "var(--ink-3)", fontWeight: 400 }}>—</span>}
          </strong>
        </div>
      </div>

      <div className="order-layout">
        <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
          <section style={card}>
            <header style={cardHead}>
              <h2 style={cardTitle}>{t("orderEditor.lines.title", "Lines")}</h2>
              <span style={{ color: "var(--ink-3)", fontSize: 12 }}>
                {totalLines === 1
                  ? t("orderEditor.lines.count_one", "{{count}} line", { count: totalLines })
                  : t("orderEditor.lines.count_other", "{{count}} lines", { count: totalLines })}
              </span>
            </header>

            {totalLines === 0 ? (
              <div style={emptyState}>
                <p style={{ margin: 0, color: "var(--ink-3)" }}>{t("orderEditor.lines.empty", "No lines yet.")}</p>
                <p style={{ margin: "6px 0 0", color: "var(--ink-4)", fontSize: 12 }}>
                  {t("orderEditor.lines.emptyHint", "Pick products from the catalog or use the search below.")}
                </p>
              </div>
            ) : (
              <table className="list-card" style={tbl}>
                <thead>
                  <tr>
                    <th style={th}>#</th>
                    <th style={{ ...th, width: 70 }} aria-label={t("orderEditor.table.image", "Image")} />
                    <th style={th}>{t("orderEditor.table.item", "Item")}</th>
                    <th style={{ ...th, textAlign: "right" }}>{t("orderEditor.table.quantity", "Quantity")}</th>
                    <th style={{ ...th, textAlign: "right" }}>{t("orderEditor.table.catalog", "Catalog")}</th>
                    {quote?.showDiscount && <th style={{ ...th, textAlign: "right" }}>{t("orderEditor.table.discountPct", "Disc %")}</th>}
                    <th style={{ ...th, textAlign: "right" }}>{t("orderEditor.table.net", "Net")}</th>
                    <th style={{ ...th, textAlign: "right" }}>{t("orderEditor.table.lineTotal", "Line total")}</th>
                    <th style={{ ...th, textAlign: "right", width: 80 }}>{t("common.actions", "Actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {(order ? order.lines : draftLines).map((l, i) => {
                    const productId = "productId" in l ? l.productId : null;
                    const product = productId ? productById.get(productId) : null;
                    const img = product?.categoryId ? imageByCategory.get(product.categoryId) ?? null : null;
                    const sku: string | null =
                      ("productSku" in l ? (l as { productSku: string | null }).productSku : null)
                      ?? product?.sku ?? null;
                    const name: string | null =
                      ("productName" in l ? (l as { productName: string | null }).productName : null)
                      ?? product?.name ?? null;
                    const lineId = "id" in l ? (l.id as string) : null;
                    const lineProductSku = "productSku" in l ? (l as { productSku: string | null }).productSku : null;
                    const lineOutOfStock = showStockWarning && stockStatus(lineProductSku) === "OUT";
                    const q = quote?.lines?.[i];
                    // Stavka koja nosi svoju cenu je zapis o tome po čemu je prodata i ona
                    // ima prednost. Živa cena iz cenovnika vredi samo dok se nacrt sastavlja:
                    // porudžbina od pre deset godina ne sme da pokaže današnji cenovnik.
                    // Stavka nacrta nema cene; sačuvana ih nosi. Odatle sužavanje.
                    const snimak: OrderLine | null = "catalogUnitPrice" in l ? (l as OrderLine) : null;
                    const cenaKataloska = snimak?.catalogUnitPrice ?? q?.catalogUnitPrice;
                    const cenaNeto = snimak?.netUnitPrice ?? q?.netUnitPrice;
                    const iznosStavke = snimak?.lineTotal ?? q?.lineTotal;
                    const valuta = snimak?.currency ?? quote?.currency;
                    const popust = snimak?.catalogUnitPrice != null
                      ? snimak.finalDiscountPct ?? snimak.contractedDiscountPct
                      : q?.contractedDiscountPct;
                    return (
                      <tr key={lineId ?? `draft-${i}`}>
                        <td style={td} className="oe-line-hide-mobile">{i + 1}</td>
                        <td style={td} className="oe-line-hide-mobile">
                          {img ? (
                            <img src={img} alt="" style={lineThumb} />
                          ) : (
                            <div style={lineThumbPlaceholder}><Ic.box /></div>
                          )}
                        </td>
                        <td style={td} data-label={t("orderEditor.table.item", "Item")}>
                          {sku && <div style={mono}>{sku}</div>}
                          <strong>{name ?? l.freeTextDescription ?? "—"}</strong>
                          {lineOutOfStock && (
                            <span style={stockOutTag}>{t("catalog.outOfStock", "Out of stock")}</span>
                          )}
                        </td>
                        <td style={{ ...td, textAlign: "right" }} data-label={t("orderEditor.table.quantity", "Quantity")}>
                          {editable && lineId ? (
                            <QtyStepper value={Number(l.quantity)} onChange={(n) => changeLineQty(lineId, n)} unit={l.unit} />
                          ) : editable && !lineId ? (
                            <span style={{ color: "var(--ink-2)" }}>{l.quantity} {l.unit}</span>
                          ) : (
                            <span>{l.quantity} {l.unit}</span>
                          )}
                        </td>
                        <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }} data-label={t("orderEditor.table.catalog", "Catalog")}>{formatUnitPrice(cenaKataloska, valuta)}</td>
                        {quote?.showDiscount && <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }} data-label={t("orderEditor.table.discountPct", "Disc %")}>{popust != null ? `${popust}%` : "—"}</td>}
                        <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }} data-label={t("orderEditor.table.net", "Net")}>{formatUnitPrice(cenaNeto, valuta)}</td>
                        <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }} data-label={t("orderEditor.table.lineTotal", "Line total")}>{money(iznosStavke, valuta)}</td>
                        <td style={{ ...td, textAlign: "right" }} className="card-actions">
                          {editable && (
                            <button
                              type="button"
                              onClick={() => lineId ? removeExistingLine(lineId) : removeDraftLine(i)}
                              className="btn ghost sm"
                              style={{ color: "var(--err)" }}
                              aria-label={t("orderEditor.removeLine", "Remove line")}
                            >
                              <Ic.x />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}

            {editable && (
              <div style={addLineBlock}>
                <h3 style={addLineTitle}>{t("orderEditor.addLine.title", "Add line")}</h3>
                <div className="addline-grid" style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) auto auto", gap: 8, alignItems: "end" }}>
                  <div style={{ position: "relative" }}>
                    <label style={fieldLabel}>{t("orderEditor.addLine.product", "Product")}</label>
                    <input
                      placeholder={t("orderEditor.addLine.searchPlaceholder", "Search SKU or name — e.g. BIOPRO 15")}
                      value={pickedProduct ? `${pickedProduct.sku} · ${pickedProduct.name}` : productSearch}
                      onChange={(e) => { setProductSearch(e.target.value); setPickedProduct(null); }}
                      className="input"
                      style={{ width: "100%" }}
                    />
                    {!pickedProduct && productSearch.length > 0 && filteredOptions.length > 0 && (
                      <div className="dd-scroll" style={typeaheadPanel}>
                        {filteredOptions.map((p) => (
                          <button
                            type="button"
                            key={p.id}
                            onClick={() => { setPickedProduct(p); setProductSearch(""); }}
                            style={typeaheadOption}
                          >
                            <span style={mono}>{p.sku}</span>
                            <span style={{ flex: 1 }}>{p.name}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <label style={fieldLabel}>{t("orderEditor.addLine.quantity", "Quantity")}</label>
                    <QtyStepper
                      value={quantity}
                      onChange={(n) => setQuantity(n)}
                      unit={pickedProduct ? productUnit(pickedProduct) : "pcs"}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (!pickedProduct) { toast.error(t("orderEditor.error.pickProduct", "Pick a product.")); return; }
                      if (isNew) addDraftLine(pickedProduct, quantity);
                      else void addLineToExisting(pickedProduct, quantity);
                    }}
                    className="btn primary"
                    disabled={busy || !pickedProduct}
                  >
                    <Ic.plus /> {t("orderEditor.addLine.add", "Add")}
                  </button>
                </div>
              </div>
            )}
          </section>
          {/* What the warehouse holds for the order's lines: for staff who see the warehouse, never the buyer. */}
          {order && !isNew && order.status !== "DRAFT" && state.status === "authenticated" && state.user.internal
            && hasPermission("section:warehouse:read") && <OrderReservations order={order} />}
        </div>

        <aside style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <section style={card}>
            <h2 style={cardTitle}>{t("orderEditor.details.title", "Order details")}</h2>
            <Field label={t("orderEditor.details.requestedDelivery", "Requested delivery")}>
              <input
                type="date"
                value={requestedDelivery}
                onChange={(e) => setRequestedDelivery(e.target.value)}
                disabled={!editable}
                className="input"
                style={{ width: "100%" }}
              />
            </Field>
            <Field label={t("orderEditor.details.orderReference", "Order reference")}>
              <input
                value={customerReference}
                onChange={(e) => setCustomerReference(e.target.value)}
                disabled={!editable}
                placeholder={t("orderEditor.details.orderReferencePlaceholder", "Your PO / reference")}
                className="input"
                style={{ width: "100%" }}
              />
            </Field>
            <Field label={t("orderEditor.details.customerNotes", "Customer notes")}>
              <textarea
                value={customerNotes}
                onChange={(e) => setCustomerNotes(e.target.value)}
                disabled={!editable}
                rows={4}
                placeholder={t("orderEditor.details.customerNotesPlaceholder", "Anything sales should know — site, special packaging, urgent windows…")}
                className="input"
                style={{ resize: "vertical", fontFamily: "inherit", width: "100%" }}
              />
            </Field>
            {order?.salesNotes && !canReview && (
              <Field label={t("orderEditor.details.salesNotes", "Sales notes")}>
                <textarea
                  value={order.salesNotes}
                  readOnly
                  rows={3}
                  className="input"
                  style={{ resize: "vertical", background: "var(--surface-2)", fontFamily: "inherit", width: "100%" }}
                />
              </Field>
            )}
          </section>

          {/* Bankom's terms of the ERP document, which only its staff see and set. */}
          {canReview && order && !isNew && <OrderErpTerms order={order} onSaved={setOrder} />}
          {/* The ERP pro forma invoice the approved order became: the customer's own document, so for all who see it. */}
          {order && !isNew && order.erpProformaNumber && ["APPROVED", "SHIPPED", "INVOICED"].includes(order.status)
            && <OrderProforma order={order} />}
          {/* Approved, and on through shipping and invoicing: the ERP's dispatch notes and invoices come back here, for
              whoever may see dispatch orders, the order's reviewers or not, and on an order brought over from the ERP. */}
          {order && !isNew && ["APPROVED", "SHIPPED", "INVOICED"].includes(order.status)
            && hasPermission("dispatch_orders:view") && <OrderDispatch order={order} />}
          {/* The buyer's own view of the same: what left and what was billed, with the lot documents released to him. */}
          {order && !isNew && state.status === "authenticated" && !state.user.internal
            && ["APPROVED", "SHIPPED", "INVOICED"].includes(order.status) && <CustomerOrderShipments orderId={order.id} />}

          <section style={card}>
            <h2 style={cardTitle}>{t("orders.addresses.title", "Delivery & billing")}</h2>
            {editable ? (
              <>
                <AddressPicker
                  label={t("orders.deliveryAddress", "Delivery address")}
                  purpose="delivery"
                  value={deliveryAddressId}
                  onChange={setDeliveryAddressId}
                  options={addresses.filter((a) => a.usableForDelivery)}
                  onAddNew={() => setAddAddressOpen("delivery")}
                />
                <AddressPicker
                  label={t("orders.billingAddress", "Billing address")}
                  purpose="billing"
                  value={billingAddressId}
                  onChange={setBillingAddressId}
                  options={addresses.filter((a) => a.usableForBilling)}
                  onAddNew={() => setAddAddressOpen("billing")}
                />
                <p style={{ margin: 0, fontSize: 11.5, color: "var(--ink-4)" }}>
                  {t("orders.addresses.hint", "Leave on default to use the company's default address. The chosen address is frozen onto the order.")}
                </p>
              </>
            ) : (
              <>
                <AddressSnapshotView
                  label={t("orders.deliveryAddress", "Delivery address")}
                  address={order?.deliveryAddress ?? null}
                />
                <AddressSnapshotView
                  label={t("orders.billingAddress", "Billing address")}
                  address={order?.billingAddress ?? null}
                  showVat
                />
              </>
            )}
          </section>

          {canReview && order && !isNew && (reviewTargets.length > 0 || waitsForAnotherSigner) && (
            <section style={{ ...card, borderColor: "var(--brand-primary)", boxShadow: "0 0 0 3px var(--brand-primary-50)" }}>
              <header style={cardHead}>
                <h2 style={cardTitle}>{t("orderEditor.salesReview.title", "Sales review")}</h2>
                <span style={{ fontSize: 11, color: "var(--ink-4)" }}>{t("orderEditor.salesReview.audited", "Audited")}</span>
              </header>
              {/* The per-status hint talks about approving, so it steps aside for the
                  banner below whenever the approval belongs to someone else. */}
              {!waitsForAnotherSigner && (
                <p style={{ margin: 0, fontSize: 12, color: "var(--ink-3)" }}>
                  {reviewHint(t, order)}
                </p>
              )}
              {outOfStockSkuList.length > 0 && (
                <div style={warnBanner}>
                  <Ic.alert />
                  <span>
                    <strong>{t("orders.stockWarning.title", "Possible stock shortage.")}</strong>{" "}
                    {t("orders.stockWarning.body", {
                      count: outOfStockSkuList.length,
                      defaultValue:
                        "{{count}} item(s) on this order may be out of stock — placeholder data until the ERP feed is connected. It isn’t blocked; review before approving.",
                    })}
                  </span>
                </div>
              )}
              {waitsForAnotherSigner && (
                <div style={signerBanner}>
                  <Ic.info width={16} height={16} style={{ flex: "none", marginTop: 2 }} />
                  <span>
                    <strong>{t("orders.approval.secondSigner.title", "This order waits for another signature.")}</strong>{" "}
                    {t("orders.approval.secondSigner.body", "An order is approved by someone other than the person who entered it: the sales director or an administrator. That is why the approval is not offered here.")}
                  </span>
                </div>
              )}
              <Field label={t("orderEditor.salesReview.notesLabel", "Sales notes (saved with the action)")}>
                <textarea
                  value={salesNotesDraft}
                  onChange={(e) => setSalesNotesDraft(e.target.value)}
                  rows={3}
                  placeholder={t("orderEditor.salesReview.notesPlaceholder", "Optional — explain the decision, ask for changes, attach a PO #…")}
                  className="input"
                  style={{ resize: "vertical", fontFamily: "inherit", width: "100%" }}
                />
              </Field>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 4 }}>
                {reviewTargets.map((to) => {
                  const meta = transitionMeta(t)[to] ?? { label: labelForStatus(t, to), tone: "neutral" as const };
                  const cls = meta.tone === "primary"
                    ? "btn primary"
                    : "btn";
                  const danger = meta.tone === "danger";
                  return (
                    <button
                      key={to}
                      type="button"
                      className={cls}
                      onClick={() => void doTransition(to)}
                      disabled={busy}
                      style={danger ? { borderColor: "var(--err)", color: "var(--err)", background: "var(--surface)" } : undefined}
                    >
                      {meta.icon}{meta.label}
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          <section style={card}>
            <h2 style={cardTitle}>{t("orderEditor.workflow.title", "Workflow")}</h2>
            {(() => {
              const terminal = order?.status === "REJECTED" || order?.status === "CANCELLED";
              const reached = wfReachedIndex(order?.status, isNew);
              return (
                <ol className="wf">
                  {wfSteps(t).map((label, i) => {
                    const state = i < reached ? "done" : i === reached ? (terminal ? "stopped" : "current") : "upcoming";
                    return <li key={label} className={`wf-${state}`}>{label}</li>;
                  })}
                </ol>
              );
            })()}
            <p style={{ margin: 0, fontSize: 12, color: "var(--ink-3)" }}>
              {t("orderEditor.workflow.note", "All orders go through Sales review before pushing to ERP — by design.")}
            </p>
          </section>

          {/* Narudžbenice koje idu proizvodnji. Odatle se i prave, pa veza porudžbina →
              proizvodnja postoji u podacima umesto samo u glavama. */}
          {order && <ProductionOrders order={order} />}

          {/* The trips this order rides on. Not rendered at all when the order
              isn't on one — an empty row would be noise, not information. */}
          {trips.length > 0 && (
            <section style={card}>
              <h2 style={cardTitle}>
                {trips.length === 1
                  ? t("orderEditor.section.trip", "Trip")
                  : t("orderEditor.section.trips", "Trips")}
              </h2>
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <span style={fieldLabel}>{t("trips.col.tripNumber", "Trip number")}</span>
                {trips.map((tr) => (
                  <Link
                    key={tr.id}
                    to={`/transport/trips/${tr.id}`}
                    style={{ fontSize: 13, color: "var(--brand-primary)" }}
                  >
                    <code>{tr.tripNumber}</code>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {!isNew && order && order.statusHistory.length > 0 && (
            <section style={card}>
              <h2 style={cardTitle}>{t("orders.timeline.title")}</h2>
              <ol style={timelineList}>
                {/* Newest first — the most recent change reads at the top. */}
                {[...order.statusHistory].reverse().map((h, i) => {
                  const palette = statusBadgePalette(h.status);
                  return (
                    <li key={`${h.status}-${h.changedAt}-${i}`} style={timelineItem}>
                      <span style={{ ...timelineDot, background: palette.fg }} />
                      <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                        <span style={timelineStatus}>
                          <OrderStatusBadge status={h.status} />
                        </span>
                        <span style={timelineMeta}>
                          {formatDateTime(h.changedAt)}
                        </span>
                        <span style={timelineMeta}>
                          {t("orders.timeline.changedBy", {
                            name: h.changedByName ?? t("orders.timeline.system"),
                          })}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </section>
          )}
        </aside>
      </div>

      <style>{`
        ol.wf{list-style:none;padding:0;margin:0 0 12px}
        ol.wf li{position:relative;padding:0 0 18px 26px;color:var(--ink-4);font-size:13px}
        ol.wf li:last-child{padding-bottom:0}
        /* connector line between dots */
        ol.wf li:not(:last-child):after{content:"";position:absolute;left:6px;top:16px;bottom:0;width:2px;background:var(--border)}
        ol.wf li.wf-done:not(:last-child):after{background:var(--brand-primary)}
        /* the dot */
        ol.wf li:before{content:"";position:absolute;left:0;top:2px;width:14px;height:14px;border-radius:50%;border:2px solid var(--border);background:var(--surface);box-sizing:border-box}
        ol.wf li.wf-done{color:var(--ink-2)}
        ol.wf li.wf-done:before{background:var(--brand-primary);border-color:var(--brand-primary)}
        ol.wf li.wf-current{color:var(--ink);font-weight:600}
        ol.wf li.wf-current:before{border-color:var(--brand-primary);box-shadow:0 0 0 3px var(--brand-primary-50)}
        ol.wf li.wf-stopped{color:var(--err);font-weight:600}
        ol.wf li.wf-stopped:before{border-color:var(--err);background:var(--err);box-shadow:0 0 0 3px var(--err-bg)}
      `}</style>

      {addAddressOpen && (
        <OrderAddAddressDialog
          purpose={addAddressOpen}
          onClose={() => setAddAddressOpen(null)}
          onSave={(body) => handleAddAddress(addAddressOpen, body)}
        />
      )}
    </form>
  );
}

/** Address select for the order pickers — options are the company book filtered
 * by purpose, plus an "Add new address" escape hatch and a "company default" row. */
function AddressPicker({
  label, purpose, value, onChange, options, onAddNew,
}: {
  label: string;
  purpose: "delivery" | "billing";
  value: string;
  onChange: (v: string) => void;
  options: CompanyAddress[];
  onAddNew: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Field label={label}>
      <div style={{ display: "flex", gap: 6 }}>
        <Select<string>
          value={value}
          onChange={onChange}
          ariaLabel={label}
          options={[
            { value: "", label: t("orders.address.useDefault", "Company default") },
            ...options.map((a) => ({ value: a.id, label: addressOptionLabel(a, purpose) })),
          ]}
          style={{ flex: 1, minWidth: 0 }}
        />
        <button type="button" className="btn sm" onClick={onAddNew}>
          {t("orders.addAddress", "+ New")}
        </button>
      </div>
    </Field>
  );
}

/** Read-only frozen snapshot shown once the order is no longer editable. */
function AddressSnapshotView({
  label, address, showVat,
}: {
  label: string;
  address: OrderAddress | null;
  showVat?: boolean;
}) {
  const { t } = useTranslation();
  if (!address) {
    return (
      <Field label={label}>
        <span style={{ fontSize: 13, color: "var(--ink-3)" }}>—</span>
      </Field>
    );
  }
  const cityLine = [address.postal, address.city].filter(Boolean).join(" ");
  return (
    <Field label={label}>
      <div style={{ fontSize: 13, color: "var(--ink-2)", lineHeight: 1.5 }}>
        {address.name && <div style={{ fontWeight: 600, color: "var(--ink)" }}>{address.name}</div>}
        {address.line1 && <div>{address.line1}</div>}
        {address.line2 && <div>{address.line2}</div>}
        {(cityLine || address.country) && <div>{[cityLine, address.country].filter(Boolean).join(", ")}</div>}
        {showVat && address.vat && (
          <div style={{ color: "var(--ink-4)", fontSize: 12 }}>{t("company.addresses.vat", "VAT")}: {address.vat}</div>
        )}
      </div>
    </Field>
  );
}

function addressOptionLabel(a: CompanyAddress, purpose: "delivery" | "billing"): string {
  const head = a.label || a.recipientName || a.addressLine1;
  const place = [a.postalCode, a.city].filter(Boolean).join(" ");
  const isDefault = purpose === "delivery" ? a.defaultDelivery : a.defaultBilling;
  return `${head}${place ? ` · ${place}` : ""}${isDefault ? " ★" : ""}`;
}

/** Inline add-address dialog on the order — the agreed customer escape hatch.
 * POSTs to the customer-gated endpoint; defaults are derived server-side. */
function OrderAddAddressDialog({
  purpose, onClose, onSave,
}: {
  purpose: "delivery" | "billing";
  onClose: () => void;
  onSave: (body: CompanyAddressBody) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [label, setLabel] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [country, setCountry] = useState("");
  const [vatNumber, setVatNumber] = useState("");
  // Default the new entry to the purpose it's being added for; the customer can
  // still tick the other so a single entry serves both (HQ = delivery + billing).
  const [usableForDelivery, setUsableForDelivery] = useState(purpose === "delivery");
  const [usableForBilling, setUsableForBilling] = useState(purpose === "billing");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (!addressLine1.trim()) { setErr(t("company.addresses.addressLine1Required", "Address line 1 is required.")); return; }
    if (!usableForDelivery && !usableForBilling) { setErr(t("company.addresses.purposeRequired", "Pick at least one purpose (delivery or billing).")); return; }
    setBusy(true);
    setErr(null);
    try {
      await onSave({
        label: label.trim() || null,
        recipientName: recipientName.trim() || null,
        addressLine1: addressLine1.trim(),
        addressLine2: addressLine2.trim() || null,
        city: city.trim() || null,
        postalCode: postalCode.trim() || null,
        country: country || null,
        vatNumber: vatNumber.trim() || null,
        usableForDelivery,
        usableForBilling,
      });
    } catch (e2) {
      setErr((e2 as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={addrOverlay} role="dialog" aria-modal="true" onClick={onClose}>
      {/* Nested inside the order <form>: this is a div, not a form, and the
          submit button is type=button — so it never submits the outer order. */}
      <div style={addrDialog} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: 0, fontSize: 16, color: "var(--ink)" }}>
          {purpose === "delivery"
            ? t("orders.address.addDeliveryTitle", "Add delivery address")
            : t("orders.address.addBillingTitle", "Add billing address")}
        </h3>
        {err && <div style={addrErr}>{err}</div>}
        <AddressFormFields
          label={label} setLabel={setLabel}
          recipientName={recipientName} setRecipientName={setRecipientName}
          addressLine1={addressLine1} setAddressLine1={setAddressLine1}
          addressLine2={addressLine2} setAddressLine2={setAddressLine2}
          city={city} setCity={setCity}
          postalCode={postalCode} setPostalCode={setPostalCode}
          country={country} setCountry={setCountry}
          vatNumber={vatNumber} setVatNumber={setVatNumber}
          usableForDelivery={usableForDelivery} setUsableForDelivery={setUsableForDelivery}
          usableForBilling={usableForBilling} setUsableForBilling={setUsableForBilling}
        />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button type="button" className="btn" onClick={onClose}>
            {t("action.cancel", "Cancel")}
          </button>
          <button type="button" className="btn primary" onClick={(e) => void submit(e)} disabled={busy}>
            {busy ? "…" : t("orders.address.save", "Add address")}
          </button>
        </div>
      </div>
    </div>
  );
}

type TransitionMeta = {
  label: string;
  tone: "primary" | "neutral" | "danger";
  icon?: React.ReactNode;
  confirm?: (orderNumber: string) => string;
};

function transitionMeta(t: TFunction): Partial<Record<OrderStatus, TransitionMeta>> {
  return {
    SUBMITTED: { label: t("orderEditor.transition.submit", "Submit for review"), tone: "neutral", icon: <Ic.send /> },
    IN_REVIEW: { label: t("orderEditor.transition.startReview", "Start review"), tone: "primary", icon: <Ic.eye /> },
    APPROVED: { label: t("orderEditor.transition.approve", "Approve"), tone: "primary", icon: <Ic.check /> },
    // REJECTED has no confirm here — doTransition opens the mandatory-reason
    // prompt instead.
    REJECTED: {
      label: t("orderEditor.transition.reject", "Reject"),
      tone: "danger",
      icon: <Ic.x />,
    },
    SHIPPED: { label: t("orderEditor.transition.markShipped", "Mark shipped"), tone: "primary", icon: <Ic.truck /> },
    INVOICED: { label: t("orderEditor.transition.markInvoiced", "Mark invoiced"), tone: "primary", icon: <Ic.check /> },
    CANCELLED: {
      label: t("orderEditor.transition.cancel", "Cancel order"),
      tone: "danger",
      icon: <Ic.x />,
      confirm: (n) => t("orderEditor.confirm.cancel", "Cancel {{orderNumber}}? This cannot be undone.", { orderNumber: n }),
    },
  };
}

function labelForStatus(t: TFunction, s: OrderStatus): string {
  return transitionMeta(t)[s]?.label ?? s;
}

function reviewHint(t: TFunction, order: Order): string {
  switch (order.status) {
    case "DRAFT":          return t("orderEditor.reviewHint.draft", "Draft — the customer can still edit. Once they submit it, you'll review here.");
    case "SUBMITTED":      return t("orderEditor.reviewHint.submitted", "Customer just submitted this. Start the review to take it off their hands.");
    case "IN_REVIEW":      return t("orderEditor.reviewHint.inReview", "You're reviewing. Approve to clear it for shipment, or reject with a note so the customer can fix it.");
    case "APPROVED":       return approvedHint(t, order);
    case "SHIPPED":        return t("orderEditor.reviewHint.shipped", "Shipped. Mark invoiced once the invoice is issued.");
    default:               return t("orderEditor.reviewHint.default", "All actions on this order are logged in the audit trail.");
  }
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13, marginTop: 12 }}>
      <span style={fieldLabel}>{label}</span>
      {children}
    </label>
  );
}

function QtyStepper({ value, onChange, unit }: { value: number; onChange: (n: number) => void; unit: string }) {
  const { t } = useTranslation();
  return (
    <div className="qty-box" style={qtyBox} role="group" aria-label={t("orderEditor.qty.group", "Quantity")}>
      <button type="button" onClick={() => onChange(Math.max(1, value - 1))} className="qty-btn" style={qtyBtn} aria-label={t("orderEditor.qty.decrease", "Decrease")}>
        <span style={{ fontSize: 14, fontWeight: 500 }}>−</span>
      </button>
      <input
        value={value}
        onChange={(e) => {
          const n = parseInt(e.target.value.replace(/[^0-9]/g, ""), 10);
          onChange(Number.isFinite(n) && n > 0 ? n : 1);
        }}
        className="qty-input" style={qtyInput}
        inputMode="numeric"
      />
      <button type="button" onClick={() => onChange(value + 1)} className="qty-btn" style={{ ...qtyBtn, borderRight: "none" }} aria-label={t("orderEditor.qty.increase", "Increase")}>
        <span style={{ fontSize: 14, fontWeight: 500 }}>+</span>
      </button>
      <span style={qtyUnit}>{unit}</span>
    </div>
  );
}

const card: React.CSSProperties = {
  background: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: "var(--r-md)",
  padding: 18,
  display: "flex",
  flexDirection: "column",
  gap: 8,
};
const cardHead: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: 4,
};
const cardTitle: React.CSSProperties = { margin: 0, fontSize: 14, color: "var(--ink)" };
const tbl: React.CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: 13, marginTop: 4 };
const th: React.CSSProperties = {
  textAlign: "left", padding: "10px 8px", color: "var(--ink-3)", fontWeight: 600,
  borderBottom: "1px solid var(--border)", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4,
};
const td: React.CSSProperties = { padding: "10px 8px", borderBottom: "1px solid var(--border)", verticalAlign: "middle" };
const lineThumb: React.CSSProperties = {
  width: 56, height: 56, objectFit: "contain", background: "var(--surface-2)",
  border: "1px solid var(--border)", borderRadius: "var(--r-sm)", padding: 4,
};
const lineThumbPlaceholder: React.CSSProperties = {
  width: 56, height: 56, display: "grid", placeItems: "center",
  background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: "var(--r-sm)",
  color: "var(--ink-4)",
};
const mono: React.CSSProperties = { fontFamily: "var(--mono)", fontSize: 11, color: "var(--ink-4)" };
const emptyState: React.CSSProperties = {
  padding: 24, textAlign: "center",
  background: "var(--surface-2)", border: "1px dashed var(--border-strong)",
  borderRadius: "var(--r-sm)",
};
const addLineBlock: React.CSSProperties = {
  marginTop: 16, paddingTop: 16, borderTop: "1px solid var(--border)",
};
const addLineTitle: React.CSSProperties = {
  margin: "0 0 8px", fontSize: 11, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.4,
};
const fieldLabel: React.CSSProperties = {
  display: "block", marginBottom: 4, fontSize: 11, color: "var(--ink-3)",
  textTransform: "uppercase", letterSpacing: 0.4,
};
const typeaheadPanel: React.CSSProperties = {
  position: "absolute", zIndex: 20, top: "calc(100% + 4px)", left: 0, right: 0,
  background: "var(--surface)", border: "1px solid var(--border)",
  borderRadius: "var(--r-sm)", boxShadow: "var(--shadow-2)", maxHeight: 320, overflowY: "auto",
};
const typeaheadOption: React.CSSProperties = {
  display: "flex", gap: 12, alignItems: "center",
  padding: "10px 12px", width: "100%", textAlign: "left",
  background: "transparent", border: "none", cursor: "pointer", fontSize: 13, color: "var(--ink)",
  borderBottom: "1px solid var(--border)",
};
const kpiStrip: React.CSSProperties = {
  display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10,
};
const importBanner: React.CSSProperties = {
  display: "flex", alignItems: "center", gap: 8,
  padding: "8px 12px", borderRadius: "var(--r-sm)",
  background: "var(--brand-primary-50)", border: "1px solid var(--border)",
  color: "var(--ink-2)", fontSize: 12.5,
};
const importBannerLink: React.CSSProperties = {
  color: "var(--brand-primary)", fontWeight: 600,
};
const warnBanner: React.CSSProperties = {
  display: "flex", alignItems: "flex-start", gap: 10,
  padding: "12px 14px", borderRadius: "var(--r-md)",
  background: "var(--tone-orange-bg)", border: "1px solid var(--tone-orange)", color: "var(--tone-orange)", fontSize: 13, lineHeight: 1.5,
};
const signerBanner: React.CSSProperties = {
  display: "flex", alignItems: "flex-start", gap: 10,
  padding: "12px 14px", borderRadius: "var(--r-md)",
  background: "var(--info-bg)", border: "1px solid var(--info)", color: "var(--info)", fontSize: 13, lineHeight: 1.5,
};
const stockOutTag: React.CSSProperties = {
  display: "inline-block", marginLeft: 6, padding: "0 6px", borderRadius: 999,
  fontSize: 10.5, fontWeight: 600,
  background: "var(--tone-orange-bg)", color: "var(--tone-orange)", border: "1px solid var(--tone-orange)", whiteSpace: "nowrap",
};
const money = (n: number | null | undefined, cur: string | null | undefined) =>
  formatMoney(n, cur);

const kpiTile: React.CSSProperties = {
  background: "var(--surface)", border: "1px solid var(--border)",
  borderRadius: "var(--r-md)", padding: "12px 14px",
  display: "flex", flexDirection: "column", gap: 4,
};
const kpiLabel: React.CSSProperties = {
  fontSize: 10, letterSpacing: 0.4, textTransform: "uppercase", color: "var(--ink-3)",
};
const kpiValueLg: React.CSSProperties = { fontSize: 18, color: "var(--ink)", display: "flex", alignItems: "center", gap: 6 };
const qtyBox: React.CSSProperties = {
  display: "inline-flex", alignItems: "stretch",
  background: "var(--surface)", border: "1px solid var(--border)",
  borderRadius: "var(--r-sm)", overflow: "hidden", height: 32,
};
const qtyBtn: React.CSSProperties = {
  width: 28, height: "100%", display: "grid", placeItems: "center",
  background: "transparent", border: "none", borderRight: "1px solid var(--border)",
  cursor: "pointer", color: "var(--ink-2)", padding: 0,
};
const qtyInput: React.CSSProperties = {
  width: 44, textAlign: "center", border: "none", borderRight: "1px solid var(--border)",
  background: "transparent", fontSize: 13, fontWeight: 600,
  fontVariantNumeric: "tabular-nums", outline: "none", color: "var(--ink)",
};
const qtyUnit: React.CSSProperties = {
  display: "inline-flex", alignItems: "center", padding: "0 10px",
  background: "var(--surface-2)", fontSize: 11, color: "var(--ink-3)",
  borderLeft: "1px solid var(--border)",
};
/** Linear happy-path stages shown in the Workflow stepper. */
const wfSteps = (t: TFunction): string[] => [
  t("orderEditor.workflow.step.draft", "Draft"),
  t("orderEditor.workflow.step.submitted", "Submitted"),
  t("orderEditor.workflow.step.inReview", "In review"),
  t("orderEditor.workflow.step.approved", "Approved"),
  t("orderEditor.workflow.step.shipped", "Shipped"),
  t("orderEditor.workflow.step.invoiced", "Invoiced"),
];

/** Index of the furthest stage an order has reached (drives done/current/upcoming). */
function wfReachedIndex(status: OrderStatus | undefined, isNew: boolean): number {
  if (isNew) return 0;
  switch (status) {
    case "INVOICED": return 5;
    case "SHIPPED": return 4;
    case "APPROVED": return 3;
    case "IN_REVIEW": return 2;
    case "SUBMITTED":
    case "REJECTED": return 1;
    default: return 0; // DRAFT, CANCELLED
  }
}
const addrOverlay: React.CSSProperties = {
  position: "fixed", inset: 0, background: "var(--overlay)",
  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60,
};
const addrDialog: React.CSSProperties = {
  width: "100%", maxWidth: 520, maxHeight: "85vh", overflowY: "auto",
  background: "var(--surface)", padding: 24, borderRadius: 12,
  boxShadow: "var(--shadow-2)", display: "flex", flexDirection: "column", gap: 14,
};
const addrErr: React.CSSProperties = {
  padding: 10, background: "var(--err-bg)", color: "var(--err)", borderRadius: 6, fontSize: 13,
};
const crumbMuted: React.CSSProperties = { color: "var(--ink-4)" };
const crumbLink: React.CSSProperties = { color: "var(--ink-3)", textDecoration: "none" };
const crumbCurrent: React.CSSProperties = { color: "var(--ink-2)", fontWeight: 600 };
const timelineList: React.CSSProperties = {
  listStyle: "none", margin: "4px 0 0", padding: 0,
  display: "flex", flexDirection: "column", gap: 12,
};
const timelineItem: React.CSSProperties = {
  display: "flex", alignItems: "flex-start", gap: 10,
  position: "relative", paddingLeft: 4,
};
const timelineDot: React.CSSProperties = {
  width: 10, height: 10, borderRadius: "50%", flexShrink: 0, marginTop: 4,
  boxShadow: "0 0 0 3px var(--surface)",
};
const timelineStatus: React.CSSProperties = { display: "inline-flex" };
const timelineMeta: React.CSSProperties = {
  fontSize: 11.5, color: "var(--ink-4)", fontVariantNumeric: "tabular-nums",
};
